import { AnimatePresence, motion, type Variants } from "motion/react";
import type { ReactNode } from "react";

interface ButtonLoaderProps {
  isVisible: boolean | undefined;
}

export function ButtonLoader({ isVisible }: ButtonLoaderProps): ReactNode {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          variants={buttonLoaderAnimationVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="absolute left-0 z-raised flex size-full items-center justify-center bg-background-default/80"
        >
          <LoadingIndicator />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function LoadingIndicator(): ReactNode {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      className="size-icon-control shrink-0 text-text-tertiary"
    >
      <g strokeWidth={4} strokeLinecap="round" stroke="currentColor">
        {LOADING_INDICATOR_LINES.map(({ opacity, rotation }) => (
          <line key={rotation} y1="17" y2="29" transform={`translate(32,32) rotate(${rotation})`}>
            <animate attributeName="stroke-opacity" dur="750ms" values={opacity} repeatCount="indefinite" />
          </line>
        ))}
      </g>
    </svg>
  );
}

const LOADING_INDICATOR_LINES = [
  { rotation: 180, opacity: "1;.85;.7;.65;.55;.45;.35;.25;.15;.1;0;1" },
  { rotation: 210, opacity: "0;1;.85;.7;.65;.55;.45;.35;.25;.15;.1;0" },
  { rotation: 240, opacity: ".1;0;1;.85;.7;.65;.55;.45;.35;.25;.15;.1" },
  { rotation: 270, opacity: ".15;.1;0;1;.85;.7;.65;.55;.45;.35;.25;.15" },
  { rotation: 300, opacity: ".25;.15;.1;0;1;.85;.7;.65;.55;.45;.35;.25" },
  { rotation: 330, opacity: ".35;.25;.15;.1;0;1;.85;.7;.65;.55;.45;.35" },
  { rotation: 0, opacity: ".45;.35;.25;.15;.1;0;1;.85;.7;.65;.55;.45" },
  { rotation: 30, opacity: ".55;.45;.35;.25;.15;.1;0;1;.85;.7;.65;.55" },
  { rotation: 60, opacity: ".65;.55;.45;.35;.25;.15;.1;0;1;.85;.7;.65" },
  { rotation: 90, opacity: ".7;.65;.55;.45;.35;.25;.15;.1;0;1;.85;.7" },
  { rotation: 120, opacity: ".85;.7;.65;.55;.45;.35;.25;.15;.1;0;1;.85" },
  { rotation: 150, opacity: "1;.85;.7;.65;.55;.45;.35;.25;.15;.1;0;1" },
] as const;

const buttonLoaderAnimationVariants: Variants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      delay: 0.2,
    },
  },
};
